import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UnsavedChangesTrackerService } from './unsaved-changes-tracker.service';
import { UnsavedChangesHostHelper } from './unsaved-changes-host.helper';

/**
 * Handles browser Back with a history trap so Back never leaves the SPA while
 * there are unsaved changes — avoiding the native "Leave site?" beforeunload
 * dialog that appears when history pops past the app.
 *
 * Flow when dirty:
 * 1. pushState adds a same-URL guard entry
 * 2. Back pops that entry → we re-push and show the custom Unsaved Changes dialog
 * 3. Stay → keep the guard; Discard → clear dirty and history.back() to leave
 */
@Injectable({ providedIn: 'root' })
export class UnsavedChangesBrowserBackService {
  private readonly tracker = inject(UnsavedChangesTrackerService);
  private readonly dialog = inject(MatDialog);

  private armed = false;
  private handlingPopstate = false;
  private clearingGuard = false;
  private started = false;

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    this.tracker.hasUnsavedChanges$.subscribe((dirty) => {
      this.syncGuard(dirty);
    });

    window.addEventListener('popstate', () => this.onPopState());
  }

  private syncGuard(dirty: boolean): void {
    if (dirty && !this.armed) {
      history.pushState({ __mcUnsavedGuard: true }, '', location.href);
      this.armed = true;
      return;
    }
    if (!dirty && this.armed) {
      this.armed = false;
      // Remove the same-URL guard entry so a later Back leaves normally.
      const state = history.state as { __mcUnsavedGuard?: boolean } | null;
      if (state?.__mcUnsavedGuard) {
        this.clearingGuard = true;
        history.back();
        queueMicrotask(() => {
          this.clearingGuard = false;
        });
      }
    }
  }

  private onPopState(): void {
    if (this.handlingPopstate || this.clearingGuard) {
      return;
    }

    if (!this.tracker.hasUnsavedChanges()) {
      this.armed = false;
      return;
    }

    // Stay on this page: re-apply the same-URL guard, then ask the user.
    this.handlingPopstate = true;
    history.pushState({ __mcUnsavedGuard: true }, '', location.href);
    this.armed = true;

    void UnsavedChangesHostHelper.openConfirm(this.dialog).then((discard) => {
      if (discard) {
        this.dialog.closeAll();
        // Disarm before clearing dirty so syncGuard does not also history.back().
        this.armed = false;
        this.tracker.requestDiscard();
        // Proceed with the original Back now that changes are discarded.
        history.back();
      }
      this.handlingPopstate = false;
    });
  }
}
